/**
 * FilePreview Component
 *
 * Full-screen file preview with support for multiple file types:
 * - Code files (syntax highlighting)
 * - Markdown (rendered with navigation sidebar)
 * - Images (zoom controls)
 * - PDF (page navigation)
 * - DOCX (rendered to HTML)
 * - XLSX (rendered as table)
 * - HTML (sandboxed iframe)
 * - Text (monospace pre-formatted)
 */

import { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useParams, useNavigate, useLocation, useSearchParams } from 'react-router-dom';
import { Light as SyntaxHighlighter } from 'react-syntax-highlighter';
import { vs2015 } from 'react-syntax-highlighter/dist/esm/styles/hljs';
import ReactMarkdown from 'react-markdown';
import type { Components } from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { Document, Page, pdfjs } from 'react-pdf';
import 'react-pdf/dist/Page/TextLayer.css';
import 'react-pdf/dist/Page/AnnotationLayer.css';
import * as XLSX from 'xlsx';

// Import markdown navigation utilities
import { parseMarkdownHeaders, buildNavigationTree } from '@/utils/markdownNav';
import type { NavigationNode } from '@/utils/markdownNav';
import { useScrollSpy } from '@/hooks/useScrollSpy';
import NavigationTree from '@/components/NavigationTree';
import { useAuth } from '@/contexts/AuthContext';

// Configure PDF.js worker
pdfjs.GlobalWorkerOptions.workerSrc = `//unpkg.com/pdfjs-dist@${pdfjs.version}/build/pdf.worker.min.mjs`;

// Import syntax highlighting languages
import javascript from 'react-syntax-highlighter/dist/esm/languages/hljs/javascript';
import typescript from 'react-syntax-highlighter/dist/esm/languages/hljs/typescript';
import python from 'react-syntax-highlighter/dist/esm/languages/hljs/python';
import json from 'react-syntax-highlighter/dist/esm/languages/hljs/json';
import sql from 'react-syntax-highlighter/dist/esm/languages/hljs/sql';
import css from 'react-syntax-highlighter/dist/esm/languages/hljs/css';
import xml from 'react-syntax-highlighter/dist/esm/languages/hljs/xml';
import bash from 'react-syntax-highlighter/dist/esm/languages/hljs/bash';
import markdown from 'react-syntax-highlighter/dist/esm/languages/hljs/markdown';
import yaml from 'react-syntax-highlighter/dist/esm/languages/hljs/yaml';

// Register languages
SyntaxHighlighter.registerLanguage('javascript', javascript);
SyntaxHighlighter.registerLanguage('typescript', typescript);
SyntaxHighlighter.registerLanguage('python', python);
SyntaxHighlighter.registerLanguage('json', json);
SyntaxHighlighter.registerLanguage('sql', sql);
SyntaxHighlighter.registerLanguage('css', css);
SyntaxHighlighter.registerLanguage('xml', xml);
SyntaxHighlighter.registerLanguage('html', xml);
SyntaxHighlighter.registerLanguage('bash', bash);
SyntaxHighlighter.registerLanguage('markdown', markdown);
SyntaxHighlighter.registerLanguage('yaml', yaml);

// Types
type FileType = 'code' | 'markdown' | 'image' | 'pdf' | 'docx' | 'xlsx' | 'html' | 'text' | 'binary';
type PdfTab = 'original' | 'ascii' | 'mining';
type DocTab = 'original' | 'markdown';

interface FilePreviewProps {
  mode: 'attachment' | 'iteration' | 'files';
}

// ASCII/Mining API response types
interface AsciiPage {
  file_id: string;
  page_number: number;
  ascii_content: string;
  visual_markers: Array<{ type: string; detected: boolean }>;
}

interface AsciiPagesResponse {
  file_id: string;
  pages: AsciiPage[];
  total_pages: number;
}

interface SenseSummary {
  file_id: string;
  summary_text: string;
  summary_length: number;
  key_topics: string[];
  entity_mentions: string[];
  pages_summarized: number | null;
  model: string;
  generated_at: string | null;
  generation_cost_usd: number | null;
}

interface AsciiJobStatus {
  job_id: string;
  file_id: string;
  status: string;
  pages_total: number | null;
  pages_processed: number | null;
  error_message: string | null;
}

// File type detection
const getFileType = (fileName: string): FileType => {
  const ext = fileName.split('.').pop()?.toLowerCase() || '';

  // Code files
  const codeExtensions = [
    'js', 'jsx', 'ts', 'tsx', 'py', 'java', 'cpp', 'c', 'h', 'cs',
    'php', 'rb', 'go', 'rs', 'swift', 'kt', 'sh', 'bash', 'sql',
    'css', 'scss', 'less', 'yaml', 'yml', 'toml', 'ini', 'conf'
  ];
  if (codeExtensions.includes(ext)) return 'code';

  // Markdown
  if (ext === 'md' || ext === 'markdown') return 'markdown';

  // Images
  const imageExtensions = ['jpg', 'jpeg', 'png', 'gif', 'bmp', 'webp', 'svg', 'ico'];
  if (imageExtensions.includes(ext)) return 'image';

  // PDF
  if (ext === 'pdf') return 'pdf';

  // DOCX
  if (ext === 'docx' || ext === 'doc') return 'docx';

  // XLSX
  if (ext === 'xlsx' || ext === 'xls' || ext === 'csv') return 'xlsx';

  // HTML
  if (ext === 'html' || ext === 'htm') return 'html';

  // JSON (special case - render as code)
  if (ext === 'json') return 'code';

  // Text files
  const textExtensions = ['txt', 'log', 'env', 'gitignore', 'dockerignore'];
  if (textExtensions.includes(ext)) return 'text';

  return 'binary';
};

// Get syntax highlighter language
const getLanguage = (fileName: string): string => {
  const ext = fileName.split('.').pop()?.toLowerCase() || '';
  const languageMap: Record<string, string> = {
    'js': 'javascript',
    'jsx': 'javascript',
    'ts': 'typescript',
    'tsx': 'typescript',
    'py': 'python',
    'json': 'json',
    'sql': 'sql',
    'css': 'css',
    'scss': 'css',
    'less': 'css',
    'html': 'html',
    'htm': 'html',
    'xml': 'xml',
    'sh': 'bash',
    'bash': 'bash',
    'md': 'markdown',
    'yaml': 'yaml',
    'yml': 'yaml',
  };
  return languageMap[ext] || 'plaintext';
};

// Format file size
const formatFileSize = (bytes: number): string => {
  if (bytes === 0) return '0 B';
  const k = 1024;
  const sizes = ['B', 'KB', 'MB', 'GB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return parseFloat((bytes / Math.pow(k, i)).toFixed(1)) + ' ' + sizes[i];
};

export default function FilePreview({ mode }: FilePreviewProps) {
  const { t } = useTranslation('files');
  const navigate = useNavigate();
  const location = useLocation();
  const params = useParams<{
    taskId: string;
    attachmentId?: string;
    iterationId?: string;
    fileId?: string;  // For 'files' mode
  }>();
  const { token } = useAuth();
  const [searchParams] = useSearchParams();
  const returnFolderId = searchParams.get('folder');

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [fileContent, setFileContent] = useState<string | ArrayBuffer | null>(null);
  const [fileBlob, setFileBlob] = useState<Blob | null>(null);
  const [fileName, setFileName] = useState<string>('');
  const [fileSize, setFileSize] = useState<number>(0);
  const [fileType, setFileType] = useState<FileType>('binary');

  // Image zoom state
  const [imageZoom, setImageZoom] = useState(100);

  // PDF state
  const [numPages, setNumPages] = useState<number>(0);
  const [currentPage, setCurrentPage] = useState(1);

  // PDF tabs state (for ASCII/Mining views)
  const [pdfTab, setPdfTab] = useState<PdfTab>('original');
  const [asciiPages, setAsciiPages] = useState<AsciiPage[]>([]);
  const [asciiLoading, setAsciiLoading] = useState(false);
  const [asciiError, setAsciiError] = useState<string | null>(null);
  const [asciiJobStatus, setAsciiJobStatus] = useState<AsciiJobStatus | null>(null);
  const [currentAsciiPage, setCurrentAsciiPage] = useState(1);
  const [senseSummary, setSenseSummary] = useState<SenseSummary | null>(null);
  const [senseLoading, setSenseLoading] = useState(false);
  const [senseError, setSenseError] = useState<string | null>(null);

  // Processing status (for knowing if ASCII/Sense/Markdown are available)
  const [processingStatus, setProcessingStatus] = useState<{
    has_ascii: boolean;
    has_sense: boolean;
    has_markdown: boolean;
    status: string;
  } | null>(null);

  // Document tabs state (for docx/xlsx markdown view)
  const [docTab, setDocTab] = useState<DocTab>('original');
  const [markdownContent, setMarkdownContent] = useState<string | null>(null);
  const [markdownLoading, setMarkdownLoading] = useState(false);
  const [markdownError, setMarkdownError] = useState<string | null>(null);

  // XLSX state
  const [xlsxHtml, setXlsxHtml] = useState<string>('');
  const [sheetNames, setSheetNames] = useState<string[]>([]);
  const [currentSheet, setCurrentSheet] = useState(0);
  const [workbook, setWorkbook] = useState<XLSX.WorkBook | null>(null);

  // DOCX container ref and pending render data
  const docxContainerRef = useRef<HTMLDivElement>(null);
  const [docxArrayBuffer, setDocxArrayBuffer] = useState<ArrayBuffer | null>(null);

  // Markdown navigation state
  const [sidebarOpen, setSidebarOpen] = useState(true);
  const sidebarRef = useRef<HTMLDivElement>(null);
  const contentRef = useRef<HTMLDivElement>(null);

  // Build navigation structure from markdown content
  const { tree, elementIds } = useMemo(() => {
    if (fileType !== 'markdown' || !fileContent || typeof fileContent !== 'string') {
      return { tree: [] as NavigationNode[], elementIds: [] as string[] };
    }
    const parsed = parseMarkdownHeaders(fileContent);
    const builtTree = buildNavigationTree(parsed);
    const ids = parsed.map((h) => h.id);
    return { tree: builtTree, elementIds: ids };
  }, [fileType, fileContent]);

  // Scroll spy for tracking active section
  const activeElementId = useScrollSpy(elementIds, { containerRef: contentRef });

  // Scroll to element in markdown content
  const scrollToElement = useCallback((elementId: string) => {
    const element = document.getElementById(elementId);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  }, []);

  // Auto-scroll sidebar to show active element
  useEffect(() => {
    if (activeElementId && sidebarRef.current) {
      const activeNavItem = sidebarRef.current.querySelector(`[data-nav-id="${activeElementId}"]`);
      if (activeNavItem) {
        const sidebar = sidebarRef.current;
        const itemRect = activeNavItem.getBoundingClientRect();
        const sidebarRect = sidebar.getBoundingClientRect();

        // Check if item is out of view
        if (itemRect.top < sidebarRect.top || itemRect.bottom > sidebarRect.bottom) {
          activeNavItem.scrollIntoView({ behavior: 'smooth', block: 'center' });
        }
      }
    }
  }, [activeElementId]);

  // Get file info from location state or fetch
  const locationState = location.state as {
    fileName?: string;
    fileSize?: number;
    downloadUrl?: string;
  } | null;

  // Fetch ASCII pages for PDF
  const fetchAsciiPages = useCallback(async (fileId: string) => {
    setAsciiLoading(true);
    setAsciiError(null);
    try {
      const response = await fetch(`/api/processing/${fileId}/ascii`, {
        headers: { Authorization: `Bearer ${token}` },
      });

      if (response.status === 404) {
        // No ASCII pages yet - check job status
        const jobResponse = await fetch(`/api/processing/${fileId}/ascii-job`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (jobResponse.ok) {
          const jobData = await jobResponse.json();
          setAsciiJobStatus(jobData);
        } else {
          setAsciiJobStatus(null);
        }
        setAsciiPages([]);
        return;
      }

      if (!response.ok) {
        throw new Error('Failed to fetch ASCII pages');
      }

      const data: AsciiPagesResponse = await response.json();
      setAsciiPages(data.pages);
      setAsciiJobStatus(null);
    } catch (err) {
      setAsciiError(err instanceof Error ? err.message : t('file_preview.failed_to_load_ascii'));
    } finally {
      setAsciiLoading(false);
    }
  }, [token]);

  // Fetch sense summary (AI insights) for PDF
  const fetchSenseSummary = useCallback(async (fileId: string) => {
    setSenseLoading(true);
    setSenseError(null);
    try {
      const response = await fetch(`/api/processing/${fileId}/sense`, {
        headers: { Authorization: `Bearer ${token}` },
      });

      if (response.status === 404) {
        setSenseSummary(null);
        return;
      }

      if (!response.ok) {
        throw new Error('Failed to fetch AI insights');
      }

      const data: SenseSummary = await response.json();
      setSenseSummary(data);
    } catch (err) {
      setSenseError(err instanceof Error ? err.message : t('file_preview.failed_to_load_ai_insights'));
    } finally {
      setSenseLoading(false);
    }
  }, [token]);

  // Fetch markdown content for documents (docx/xlsx)
  const fetchMarkdown = useCallback(async (fileId: string) => {
    setMarkdownLoading(true);
    setMarkdownError(null);
    try {
      const response = await fetch(`/api/processing/${fileId}/markdown`, {
        headers: { Authorization: `Bearer ${token}` },
      });

      if (response.status === 404) {
        setMarkdownContent(null);
        return;
      }

      if (!response.ok) {
        throw new Error('Failed to fetch markdown');
      }

      const data = await response.json();
      setMarkdownContent(data.markdown);
    } catch (err) {
      setMarkdownError(err instanceof Error ? err.message : t('file_preview.failed_to_load_markdown'));
    } finally {
      setMarkdownLoading(false);
    }
  }, [token]);

  // Trigger ASCII rendering
  const triggerAsciiRendering = useCallback(async (fileId: string) => {
    try {
      const response = await fetch(`/api/processing/${fileId}/ascii`, {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({}),
      });

      if (!response.ok) {
        throw new Error('Failed to trigger ASCII rendering');
      }

      const data = await response.json();
      setAsciiJobStatus(data);

      // Poll for completion
      const pollInterval = setInterval(async () => {
        const statusResponse = await fetch(`/api/processing/${fileId}/ascii-job`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (statusResponse.ok) {
          const status = await statusResponse.json();
          setAsciiJobStatus(status);
          if (status.status === 'completed' || status.status === 'failed') {
            clearInterval(pollInterval);
            if (status.status === 'completed') {
              fetchAsciiPages(fileId);
            }
          }
        }
      }, 5000); // Poll every 5 seconds

      // Stop polling after 10 minutes
      setTimeout(() => clearInterval(pollInterval), 600000);
    } catch (err) {
      setAsciiError(err instanceof Error ? err.message : t('file_preview.failed_to_trigger_ascii'));
    }
  }, [fetchAsciiPages, token]);

  // Fetch file content
  const fetchFile = useCallback(async () => {
    setLoading(true);
    setError(null);

    try {
      // Get download URL from location state or fetch from API
      let downloadUrl = locationState?.downloadUrl;
      let name = locationState?.fileName || 'file';
      let size = locationState?.fileSize || 0;

      if (!downloadUrl) {
        // Fetch download URL from API based on mode
        if (mode === 'files' && params.fileId) {
          // Files module - use filesApi
          const { getDownloadUrl, getFile } = await import('@/services/filesApi');

          // Get file info for name and size if not in location state
          if (!locationState?.fileName) {
            const fileInfo = await getFile(params.fileId);
            name = fileInfo.name;
            size = fileInfo.file_size || 0;
          }

          const response = await getDownloadUrl(params.fileId);
          downloadUrl = response.url;
        } else if (mode === 'attachment' && params.taskId && params.attachmentId) {
          const { getAttachmentDownloadUrl } = await import('@/services/api');
          const response = await getAttachmentDownloadUrl(params.taskId, params.attachmentId);
          downloadUrl = response.url;
        } else if (mode === 'iteration' && params.taskId && params.iterationId && params.attachmentId) {
          const { getIterationAttachmentDownloadUrl } = await import('@/services/api');
          const response = await getIterationAttachmentDownloadUrl(
            params.taskId,
            params.iterationId,
            params.attachmentId
          );
          downloadUrl = response.url;
        } else {
          throw new Error('Missing required parameters');
        }
      }

      setFileName(name);
      setFileSize(size);

      const detectedType = getFileType(name);
      setFileType(detectedType);

      // Fetch the file
      const response = await fetch(downloadUrl);
      if (!response.ok) {
        throw new Error(`Failed to fetch file: ${response.status}`);
      }

      const blob = await response.blob();
      setFileBlob(blob);
      setFileSize(blob.size);

      // Process based on file type
      if (detectedType === 'image') {
        // For images, create object URL
        setFileContent(URL.createObjectURL(blob));
      } else if (detectedType === 'pdf') {
        // For PDF, keep as blob
        setFileContent(URL.createObjectURL(blob));
      } else if (detectedType === 'docx') {
        // For DOCX, store arrayBuffer - rendering happens in useEffect when ref is ready
        const arrayBuffer = await blob.arrayBuffer();
        setDocxArrayBuffer(arrayBuffer);
        setFileContent('docx-pending');
      } else if (detectedType === 'xlsx') {
        // For XLSX, parse and render as HTML table
        const arrayBuffer = await blob.arrayBuffer();
        const wb = XLSX.read(arrayBuffer, { type: 'array' });
        setWorkbook(wb);
        setSheetNames(wb.SheetNames);
        setCurrentSheet(0);
        const html = XLSX.utils.sheet_to_html(wb.Sheets[wb.SheetNames[0]], {
          editable: false,
        });
        setXlsxHtml(html);
        setFileContent('xlsx-rendered');
      } else if (detectedType === 'binary') {
        // For binary files, just show download option
        setFileContent(null);
      } else {
        // For text-based files, read as text
        const text = await blob.text();
        setFileContent(text);
      }
    } catch (err) {
      console.error('Failed to load file:', err);
      setError(err instanceof Error ? err.message : t('file_preview.failed_to_load'));
    } finally {
      setLoading(false);
    }
  }, [mode, params, locationState]);

  useEffect(() => {
    fetchFile();

    // Cleanup object URLs on unmount
    return () => {
      if (fileContent && typeof fileContent === 'string' && fileContent.startsWith('blob:')) {
        URL.revokeObjectURL(fileContent);
      }
    };
  }, [fetchFile]);

  // Fetch processing status for PDF/DOCX/XLSX files to know if ASCII/Sense/Markdown are available
  useEffect(() => {
    if (['pdf', 'docx', 'xlsx'].includes(fileType) && mode === 'files' && params.fileId && token) {
      const fetchProcessingStatus = async () => {
        try {
          const response = await fetch(`/api/processing/${params.fileId}/processing-status`, {
            headers: { Authorization: `Bearer ${token}` },
          });
          if (response.ok) {
            const data = await response.json();
            setProcessingStatus({
              has_ascii: data.has_ascii || false,
              has_sense: data.has_sense || false,
              has_markdown: data.has_markdown || false,
              status: data.status || 'pending',
            });
          }
        } catch (err) {
          console.error('Failed to fetch processing status:', err);
        }
      };
      fetchProcessingStatus();
    }
  }, [fileType, mode, params.fileId, token]);

  // Render DOCX when container ref and data are both available
  useEffect(() => {
    const renderDocx = async () => {
      if (docxArrayBuffer && docxContainerRef.current && fileType === 'docx' && !loading) {
        try {
          const { renderAsync } = await import('docx-preview');
          // Clear previous content
          docxContainerRef.current.innerHTML = '';
          await renderAsync(docxArrayBuffer, docxContainerRef.current, undefined, {
            className: 'docx-preview-container',
            inWrapper: true,
            ignoreWidth: false,
            ignoreHeight: false,
            ignoreFonts: false,
            breakPages: true,
            ignoreLastRenderedPageBreak: true,
            experimental: false,
            trimXmlDeclaration: true,
            useBase64URL: true,
            renderHeaders: true,
            renderFooters: true,
            renderFootnotes: true,
            renderEndnotes: true,
          });
          setFileContent('docx-rendered');
        } catch (err) {
          console.error('Failed to render DOCX:', err);
        }
      }
    };
    renderDocx();
  }, [docxArrayBuffer, fileType, loading]);

  // Fetch ASCII/sense data when tab changes for PDF
  useEffect(() => {
    if (fileType === 'pdf' && mode === 'files' && params.fileId) {
      if (pdfTab === 'ascii' && asciiPages.length === 0 && !asciiLoading) {
        fetchAsciiPages(params.fileId);
      } else if (pdfTab === 'mining' && !senseSummary && !senseLoading) {
        fetchSenseSummary(params.fileId);
      }
    }
  }, [pdfTab, fileType, mode, params.fileId, asciiPages.length, senseSummary, asciiLoading, senseLoading, fetchAsciiPages, fetchSenseSummary]);

  // Fetch markdown when doc tab changes for docx/xlsx
  useEffect(() => {
    if (['docx', 'xlsx'].includes(fileType) && mode === 'files' && params.fileId) {
      if (docTab === 'markdown' && !markdownContent && !markdownLoading) {
        fetchMarkdown(params.fileId);
      }
    }
  }, [docTab, fileType, mode, params.fileId, markdownContent, markdownLoading, fetchMarkdown]);

  // Handle sheet change for XLSX
  const handleSheetChange = (index: number) => {
    if (workbook && workbook.SheetNames[index]) {
      setCurrentSheet(index);
      const html = XLSX.utils.sheet_to_html(workbook.Sheets[workbook.SheetNames[index]], {
        editable: false,
      });
      setXlsxHtml(html);
    }
  };

  // Handle close
  const handleClose = () => {
    if (params.taskId) {
      // Return to task page (works for both attachment and iteration modes)
      navigate(`/tasks/${params.taskId}`);
    } else if (returnFolderId) {
      navigate(`/disk?folder=${returnFolderId}`);
    } else {
      navigate('/disk');
    }
  };

  // Handle download
  const handleDownload = () => {
    if (fileBlob && fileName) {
      const url = URL.createObjectURL(fileBlob);
      const a = document.createElement('a');
      a.href = url;
      a.download = fileName;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    }
  };

  // Render content based on file type
  const renderContent = () => {
    if (loading) {
      return (
        <div className="flex items-center justify-center h-full">
          <div className="flex flex-col items-center gap-4">
            <div className="w-10 h-10 border-4 border-blue-200 border-t-blue-600 rounded-full animate-spin" />
            <p className="text-gray-500">{t('file_preview.loading_file')}</p>
          </div>
        </div>
      );
    }

    if (error) {
      return (
        <div className="flex items-center justify-center h-full">
          <div className="text-center">
            <div className="text-6xl mb-4">:(</div>
            <h2 className="text-xl font-semibold mb-2">{t('file_preview.failed_to_load')}</h2>
            <p className="text-gray-500 mb-6">{error}</p>
            <button
              onClick={handleClose}
              className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
            >
              {t('file_preview.go_back')}
            </button>
          </div>
        </div>
      );
    }

    switch (fileType) {
      case 'code':
        return (
          <div className="h-full overflow-auto bg-[#1e1e1e]">
            <SyntaxHighlighter
              language={getLanguage(fileName)}
              style={vs2015}
              showLineNumbers
              wrapLines
              customStyle={{
                margin: 0,
                padding: '1rem',
                fontSize: '14px',
                lineHeight: '1.5',
                minHeight: '100%',
              }}
            >
              {String(fileContent)}
            </SyntaxHighlighter>
          </div>
        );

      case 'markdown': {
        // Create ID counter for handling duplicate headers (matches parseMarkdownHeaders logic)
        const idCounts: Record<string, number> = {};

        // Helper to generate unique header ID
        const getHeaderId = (text: string): string => {
          const baseId = String(text)
            .toLowerCase()
            .replace(/[^a-z0-9\s-]/g, '')
            .replace(/\s+/g, '-')
            .replace(/-+/g, '-')
            .replace(/^-|-$/g, '');

          let id = baseId;
          if (idCounts[baseId] !== undefined) {
            idCounts[baseId]++;
            id = `${baseId}-${idCounts[baseId]}`;
          } else {
            idCounts[baseId] = 0;
          }
          return id;
        };

        // Create custom markdown components with anchor IDs
        const markdownComponents: Partial<Components> = {
          h1: ({ children, ...props }) => (
            <h1 id={getHeaderId(String(children))} className="scroll-mt-20" {...props}>{children}</h1>
          ),
          h2: ({ children, ...props }) => (
            <h2 id={getHeaderId(String(children))} className="scroll-mt-20" {...props}>{children}</h2>
          ),
          h3: ({ children, ...props }) => (
            <h3 id={getHeaderId(String(children))} className="scroll-mt-20" {...props}>{children}</h3>
          ),
          h4: ({ children, ...props }) => (
            <h4 id={getHeaderId(String(children))} className="scroll-mt-20" {...props}>{children}</h4>
          ),
          h5: ({ children, ...props }) => (
            <h5 id={getHeaderId(String(children))} className="scroll-mt-20" {...props}>{children}</h5>
          ),
          h6: ({ children, ...props }) => (
            <h6 id={getHeaderId(String(children))} className="scroll-mt-20" {...props}>{children}</h6>
          ),
        };

        return (
          <div className="h-full flex">
            {/* Sidebar Navigation */}
            {tree.length > 0 && (
              <div
                className={`flex-shrink-0 bg-white border-r border-gray-200 transition-all duration-300 ${
                  sidebarOpen ? 'w-72' : 'w-0'
                }`}
              >
                {sidebarOpen && (
                  <div ref={sidebarRef} className="h-full overflow-y-auto p-4">
                    <div className="flex justify-between items-center mb-4">
                      <h3 className="font-semibold text-sm text-gray-700">{t('file_preview.navigation')}</h3>
                      <button
                        onClick={() => setSidebarOpen(false)}
                        className="p-1 text-gray-400 hover:text-gray-600 rounded"
                        title={t('file_preview.close_sidebar')}
                      >
                        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                        </svg>
                      </button>
                    </div>

                    <NavigationTree
                      nodes={tree}
                      activeId={activeElementId}
                      onNavigate={scrollToElement}
                      title={t('file_preview.contents')}
                      showExpandAll={tree.length > 3}
                    />
                  </div>
                )}
              </div>
            )}

            {/* Toggle sidebar button (when closed) */}
            {!sidebarOpen && tree.length > 0 && (
              <button
                onClick={() => setSidebarOpen(true)}
                className="absolute left-4 top-20 z-10 p-2 bg-white border border-gray-200 rounded-lg shadow-md hover:bg-gray-50"
                title={t('file_preview.open_navigation')}
              >
                <svg className="w-5 h-5 text-gray-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
                </svg>
              </button>
            )}

            {/* Main content */}
            <div ref={contentRef} className="flex-1 overflow-auto bg-gray-50 p-8">
              <div className="max-w-4xl mx-auto bg-white rounded-lg shadow-lg overflow-hidden">
                <div className="bg-gradient-to-r from-blue-500 to-purple-600 px-6 py-4">
                  <h2 className="text-white font-semibold text-lg">{fileName}</h2>
                </div>
                <div className="markdown-content p-8">
                  <ReactMarkdown
                    remarkPlugins={[remarkGfm]}
                    components={markdownComponents}
                  >
                    {String(fileContent)}
                  </ReactMarkdown>
                </div>
              </div>
            </div>
          </div>
        );
      }

      case 'image':
        return (
          <div className="h-full overflow-auto flex items-center justify-center bg-gray-100 p-4">
            <img
              src={String(fileContent)}
              alt={fileName}
              style={{ transform: `scale(${imageZoom / 100})` }}
              className="max-w-full max-h-full object-contain transition-transform"
            />
          </div>
        );

      case 'pdf':
        // Render based on active tab
        if (pdfTab === 'ascii') {
          return (
            <div className="h-full overflow-auto bg-gray-900 p-4">
              {asciiLoading ? (
                <div className="flex items-center justify-center h-full">
                  <div className="flex flex-col items-center gap-4">
                    <div className="w-10 h-10 border-4 border-blue-200 border-t-blue-600 rounded-full animate-spin" />
                    <p className="text-gray-400">{t('file_preview.loading_ascii')}</p>
                  </div>
                </div>
              ) : asciiError ? (
                <div className="flex items-center justify-center h-full">
                  <div className="text-center text-red-400">
                    <p>{asciiError}</p>
                  </div>
                </div>
              ) : asciiPages.length === 0 ? (
                <div className="flex items-center justify-center h-full">
                  <div className="text-center">
                    <div className="text-6xl mb-4">📝</div>
                    <h2 className="text-xl font-semibold mb-2 text-white">{t('file_preview.no_ascii_yet')}</h2>
                    {asciiJobStatus ? (
                      <div className="text-gray-400 mb-6">
                        <p>{t('file_preview.ascii_status')} <span className="text-blue-400">{asciiJobStatus.status}</span></p>
                        {asciiJobStatus.pages_processed !== null && asciiJobStatus.pages_total !== null && (
                          <p>{t('file_preview.ascii_progress', { processed: asciiJobStatus.pages_processed, total: asciiJobStatus.pages_total })}</p>
                        )}
                        {asciiJobStatus.error_message && (
                          <p className="text-red-400 mt-2">{asciiJobStatus.error_message}</p>
                        )}
                      </div>
                    ) : (
                      <p className="text-gray-400 mb-6">
                        {t('file_preview.generate_ascii_desc')}
                      </p>
                    )}
                    {(!asciiJobStatus || asciiJobStatus.status === 'failed') && mode === 'files' && params.fileId && (
                      <button
                        onClick={() => triggerAsciiRendering(params.fileId!)}
                        className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
                      >
                        {t('file_preview.generate_ascii_button')}
                      </button>
                    )}
                  </div>
                </div>
              ) : (
                <div className="max-w-6xl mx-auto">
                  {/* ASCII content */}
                  <pre className="font-mono text-xs text-green-400 bg-black p-4 rounded-lg overflow-x-auto whitespace-pre">
                    {asciiPages[currentAsciiPage - 1]?.ascii_content}
                  </pre>
                  {/* Visual markers */}
                  {asciiPages[currentAsciiPage - 1]?.visual_markers?.length > 0 && (
                    <div className="mt-4 p-3 bg-gray-800 rounded-lg">
                      <h4 className="text-sm font-semibold text-gray-300 mb-2">{t('file_preview.visual_markers')}</h4>
                      <div className="flex flex-wrap gap-2">
                        {asciiPages[currentAsciiPage - 1].visual_markers.map((marker, i) => (
                          <span key={i} className="px-2 py-1 bg-gray-700 text-gray-300 rounded text-xs">
                            {marker.type}
                          </span>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              )}
            </div>
          );
        }

        if (pdfTab === 'mining') {
          return (
            <div className="h-full overflow-auto bg-gray-900 p-4">
              {senseLoading ? (
                <div className="flex items-center justify-center h-full">
                  <div className="flex flex-col items-center gap-4">
                    <div className="w-10 h-10 border-4 border-blue-200 border-t-blue-600 rounded-full animate-spin" />
                    <p className="text-gray-400">{t('file_preview.loading_ai_insights')}</p>
                  </div>
                </div>
              ) : senseError ? (
                <div className="flex items-center justify-center h-full">
                  <div className="text-center text-red-400">
                    <p>{senseError}</p>
                  </div>
                </div>
              ) : !senseSummary ? (
                <div className="flex items-center justify-center h-full">
                  <div className="text-center">
                    <div className="text-6xl mb-4">🧠</div>
                    <h2 className="text-xl font-semibold mb-2 text-white">{t('file_preview.no_ai_insights')}</h2>
                    <p className="text-gray-400 mb-6 max-w-md">
                      {t('file_preview.no_ai_insights_desc')}
                    </p>
                  </div>
                </div>
              ) : (
                <div className="max-w-4xl mx-auto">
                  {/* Header */}
                  <div className="bg-gradient-to-r from-purple-600 to-indigo-600 rounded-t-lg px-6 py-4">
                    <h3 className="text-white font-semibold text-lg flex items-center gap-2">
                      <span>🧠</span> {t('file_preview.ai_document_analysis')}
                    </h3>
                    <p className="text-purple-200 text-sm">
                      {senseSummary.pages_summarized ? `${senseSummary.pages_summarized} pages • ` : ''}
                      {senseSummary.summary_length.toLocaleString()} characters
                    </p>
                  </div>

                  {/* Summary */}
                  <div className="bg-gray-800 p-6 border-x border-gray-700">
                    <h4 className="text-gray-300 font-medium mb-3">{t('file_preview.summary')}</h4>
                    <p className="text-gray-100 leading-relaxed whitespace-pre-wrap">
                      {senseSummary.summary_text}
                    </p>
                  </div>

                  {/* Key Topics */}
                  {senseSummary.key_topics && senseSummary.key_topics.length > 0 && (
                    <div className="bg-gray-800 p-6 border-x border-t border-gray-700">
                      <h4 className="text-gray-300 font-medium mb-3">{t('file_preview.key_topics')}</h4>
                      <div className="flex flex-wrap gap-2">
                        {senseSummary.key_topics.map((topic, i) => (
                          <span
                            key={i}
                            className="px-3 py-1 bg-purple-900/50 text-purple-200 rounded-full text-sm"
                          >
                            {topic}
                          </span>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Entity Mentions */}
                  {senseSummary.entity_mentions && senseSummary.entity_mentions.length > 0 && (
                    <div className="bg-gray-800 p-6 border-x border-t border-gray-700">
                      <h4 className="text-gray-300 font-medium mb-3">{t('file_preview.entities_mentioned')}</h4>
                      <div className="flex flex-wrap gap-2">
                        {senseSummary.entity_mentions.map((entity, i) => (
                          <span
                            key={i}
                            className="px-3 py-1 bg-blue-900/50 text-blue-200 rounded-full text-sm"
                          >
                            {entity}
                          </span>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Metadata */}
                  {senseSummary.generated_at && (
                    <div className="bg-gray-800 rounded-b-lg p-6 border border-gray-700">
                      <p className="text-sm text-gray-500">
                        {t('file_preview.generated_label')} {new Date(senseSummary.generated_at).toLocaleString()}
                      </p>
                    </div>
                  )}
                </div>
              )}
            </div>
          );
        }

        // Original PDF view
        return (
          <div className="h-full overflow-auto flex flex-col items-center bg-gray-100 p-4">
            <Document
              file={String(fileContent)}
              onLoadSuccess={({ numPages }) => setNumPages(numPages)}
              loading={
                <div className="flex items-center gap-2">
                  <div className="w-6 h-6 border-2 border-blue-200 border-t-blue-600 rounded-full animate-spin" />
                  <span>{t('file_preview.loading_pdf')}</span>
                </div>
              }
            >
              <Page
                pageNumber={currentPage}
                renderTextLayer
                renderAnnotationLayer
                className="shadow-lg"
              />
            </Document>
          </div>
        );

      case 'docx':
        // Both views rendered, visibility controlled by docTab
        return (
          <>
            {/* Markdown tab view */}
            {docTab === 'markdown' && processingStatus?.has_markdown && (
              <div className="h-full overflow-auto bg-gray-50 p-8">
                <div className="max-w-4xl mx-auto bg-white rounded-lg shadow-lg overflow-hidden">
                  <div className="bg-gradient-to-r from-blue-500 to-purple-600 px-6 py-4">
                    <h2 className="text-white font-semibold text-lg">{fileName} - Markdown</h2>
                  </div>
                  {markdownLoading ? (
                    <div className="flex items-center justify-center p-16">
                      <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500" />
                    </div>
                  ) : markdownError ? (
                    <div className="text-red-500 text-center p-8">{markdownError}</div>
                  ) : !markdownContent ? (
                    <div className="text-gray-500 text-center p-8">{t('file_preview.no_markdown')}</div>
                  ) : (
                    <div className="markdown-content p-8">
                      <ReactMarkdown remarkPlugins={[remarkGfm]}>
                        {markdownContent}
                      </ReactMarkdown>
                    </div>
                  )}
                </div>
              </div>
            )}
            {/* Original view - always in DOM for ref, hidden when markdown tab active */}
            <div
              className={`h-full overflow-auto p-4 ${docTab === 'markdown' && processingStatus?.has_markdown ? 'hidden' : ''}`}
              style={{ backgroundColor: '#808080' }}
            >
              <div
                ref={docxContainerRef}
                className="max-w-4xl mx-auto bg-white"
                style={{ minHeight: '100%' }}
              />
            </div>
          </>
        );

      case 'xlsx':
        // Markdown tab view
        if (docTab === 'markdown' && processingStatus?.has_markdown) {
          return (
            <div className="h-full overflow-auto bg-gray-50 p-8">
              <div className="max-w-4xl mx-auto bg-white rounded-lg shadow-lg overflow-hidden">
                <div className="bg-gradient-to-r from-blue-500 to-purple-600 px-6 py-4">
                  <h2 className="text-white font-semibold text-lg">{fileName} - Markdown</h2>
                </div>
                {markdownLoading ? (
                  <div className="flex items-center justify-center p-16">
                    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500" />
                  </div>
                ) : markdownError ? (
                  <div className="text-red-500 text-center p-8">{markdownError}</div>
                ) : !markdownContent ? (
                  <div className="text-gray-500 text-center p-8">{t('file_preview.no_markdown')}</div>
                ) : (
                  <div className="markdown-content p-8">
                    <ReactMarkdown remarkPlugins={[remarkGfm]}>
                      {markdownContent}
                    </ReactMarkdown>
                  </div>
                )}
              </div>
            </div>
          );
        }
        // Original view
        return (
          <div className="h-full overflow-auto bg-white">
            <div
              className="xlsx-preview p-4"
              dangerouslySetInnerHTML={{ __html: xlsxHtml }}
            />
          </div>
        );

      case 'html':
        return (
          <div className="h-full bg-white">
            <iframe
              srcDoc={String(fileContent)}
              title={fileName}
              className="w-full h-full border-0"
              sandbox="allow-same-origin"
            />
          </div>
        );

      case 'text':
        return (
          <div className="h-full overflow-auto bg-gray-50 p-4">
            <pre className="font-mono text-sm text-gray-800 whitespace-pre-wrap break-words">
              {String(fileContent)}
            </pre>
          </div>
        );

      case 'binary':
      default:
        return (
          <div className="flex items-center justify-center h-full">
            <div className="text-center">
              <div className="text-6xl mb-4">📄</div>
              <h2 className="text-xl font-semibold mb-2">{t('file_preview.not_available')}</h2>
              <p className="text-gray-500 mb-6">
                {t('file_preview.not_available_desc')}
              </p>
              <button
                onClick={handleDownload}
                className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
              >
                {t('file_preview.download_file')}
              </button>
            </div>
          </div>
        );
    }
  };

  return (
    <div className="fixed inset-0 z-50 bg-white flex flex-col">
      {/* Header */}
      <div className="flex-shrink-0 h-14 bg-gray-900 text-white flex items-center justify-between px-4">
        <div className="flex items-center gap-4">
          <button
            onClick={handleClose}
            className="p-2 hover:bg-gray-700 rounded-lg transition-colors"
            title={t('file_preview.close')}
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
          <div className="flex items-center gap-3">
            <span className="font-medium truncate max-w-md">{fileName}</span>
            {fileSize > 0 && (
              <span className="text-sm text-gray-400">{formatFileSize(fileSize)}</span>
            )}
          </div>
        </div>

        <div className="flex items-center gap-2">
          {/* Markdown sidebar toggle */}
          {fileType === 'markdown' && tree.length > 0 && !loading && (
            <button
              onClick={() => setSidebarOpen(!sidebarOpen)}
              className={`p-1.5 rounded transition-colors mr-2 ${
                sidebarOpen ? 'bg-blue-600 text-white' : 'hover:bg-gray-700'
              }`}
              title={sidebarOpen ? t('file_preview.hide_navigation') : t('file_preview.show_navigation')}
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h8m-8 6h16" />
              </svg>
            </button>
          )}

          {/* Image zoom controls */}
          {fileType === 'image' && !loading && (
            <div className="flex items-center gap-2 mr-4">
              <button
                onClick={() => setImageZoom(Math.max(25, imageZoom - 25))}
                disabled={imageZoom <= 25}
                className="p-1.5 hover:bg-gray-700 rounded disabled:opacity-50"
                title={t('file_preview.zoom_out')}
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M20 12H4" />
                </svg>
              </button>
              <span className="text-sm w-12 text-center">{imageZoom}%</span>
              <button
                onClick={() => setImageZoom(Math.min(200, imageZoom + 25))}
                disabled={imageZoom >= 200}
                className="p-1.5 hover:bg-gray-700 rounded disabled:opacity-50"
                title={t('file_preview.zoom_in')}
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                </svg>
              </button>
              <button
                onClick={() => setImageZoom(100)}
                className="px-2 py-1 text-xs hover:bg-gray-700 rounded"
                title={t('file_preview.reset_zoom')}
              >
                {t('file_preview.reset')}
              </button>
            </div>
          )}

          {/* PDF view tabs */}
          {fileType === 'pdf' && mode === 'files' && !loading && (
            <div className="flex items-center gap-1 mr-4 bg-gray-800 rounded-lg p-1">
              <button
                onClick={() => setPdfTab('original')}
                className={`px-3 py-1 text-sm rounded transition-colors ${
                  pdfTab === 'original'
                    ? 'bg-blue-600 text-white'
                    : 'hover:bg-gray-700 text-gray-300'
                }`}
              >
                {t('file_preview.tab_original')}
              </button>
              <button
                onClick={() => processingStatus?.has_ascii && setPdfTab('ascii')}
                disabled={!processingStatus?.has_ascii}
                className={`px-3 py-1 text-sm rounded transition-colors ${
                  pdfTab === 'ascii'
                    ? 'bg-blue-600 text-white'
                    : !processingStatus?.has_ascii
                    ? 'text-gray-500 cursor-not-allowed'
                    : 'hover:bg-gray-700 text-gray-300'
                }`}
                title={!processingStatus?.has_ascii ? t('file_preview.ascii_not_available') : ''}
              >
                {t('file_preview.tab_ascii')}
              </button>
              <button
                onClick={() => processingStatus?.has_sense && setPdfTab('mining')}
                disabled={!processingStatus?.has_sense}
                className={`px-3 py-1 text-sm rounded transition-colors ${
                  pdfTab === 'mining'
                    ? 'bg-blue-600 text-white'
                    : !processingStatus?.has_sense
                    ? 'text-gray-500 cursor-not-allowed'
                    : 'hover:bg-gray-700 text-gray-300'
                }`}
                title={!processingStatus?.has_sense ? t('file_preview.ai_insights_not_available') : ''}
              >
                {t('file_preview.tab_ai_insights')}
              </button>
            </div>
          )}

          {/* Document view tabs (docx/xlsx) */}
          {['docx', 'xlsx'].includes(fileType) && mode === 'files' && !loading && processingStatus?.has_markdown && (
            <div className="flex items-center gap-1 mr-4 bg-gray-800 rounded-lg p-1">
              <button
                onClick={() => setDocTab('original')}
                className={`px-3 py-1 text-xs font-medium rounded transition-colors ${
                  docTab === 'original'
                    ? 'bg-blue-600 text-white'
                    : 'text-gray-400 hover:text-white hover:bg-gray-700'
                }`}
              >
                {t('file_preview.tab_original')}
              </button>
              <button
                onClick={() => setDocTab('markdown')}
                className={`px-3 py-1 text-xs font-medium rounded transition-colors ${
                  docTab === 'markdown'
                    ? 'bg-blue-600 text-white'
                    : 'text-gray-400 hover:text-white hover:bg-gray-700'
                }`}
              >
                {t('file_preview.tab_markdown')}
              </button>
            </div>
          )}

          {/* PDF page controls - Original tab */}
          {fileType === 'pdf' && numPages > 0 && pdfTab === 'original' && (
            <div className="flex items-center gap-2 mr-4">
              <button
                onClick={() => setCurrentPage(Math.max(1, currentPage - 1))}
                disabled={currentPage <= 1}
                className="p-1.5 hover:bg-gray-700 rounded disabled:opacity-50"
                title={t('file_preview.previous_page')}
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                </svg>
              </button>
              <span className="text-sm">
                {currentPage} / {numPages}
              </span>
              <button
                onClick={() => setCurrentPage(Math.min(numPages, currentPage + 1))}
                disabled={currentPage >= numPages}
                className="p-1.5 hover:bg-gray-700 rounded disabled:opacity-50"
                title={t('file_preview.next_page')}
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                </svg>
              </button>
            </div>
          )}

          {/* PDF page controls - ASCII tab */}
          {fileType === 'pdf' && asciiPages.length > 0 && pdfTab === 'ascii' && (
            <div className="flex items-center gap-2 mr-4">
              <button
                onClick={() => setCurrentAsciiPage(Math.max(1, currentAsciiPage - 1))}
                disabled={currentAsciiPage <= 1}
                className="p-1.5 hover:bg-gray-700 rounded disabled:opacity-50"
                title={t('file_preview.previous_page')}
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                </svg>
              </button>
              <span className="text-sm">
                {currentAsciiPage} / {asciiPages.length}
              </span>
              <button
                onClick={() => setCurrentAsciiPage(Math.min(asciiPages.length, currentAsciiPage + 1))}
                disabled={currentAsciiPage >= asciiPages.length}
                className="p-1.5 hover:bg-gray-700 rounded disabled:opacity-50"
                title={t('file_preview.next_page')}
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                </svg>
              </button>
            </div>
          )}

          {/* XLSX sheet tabs */}
          {fileType === 'xlsx' && sheetNames.length > 1 && (
            <div className="flex items-center gap-1 mr-4">
              {sheetNames.map((name, index) => (
                <button
                  key={name}
                  onClick={() => handleSheetChange(index)}
                  className={`px-3 py-1 text-sm rounded transition-colors ${
                    currentSheet === index
                      ? 'bg-blue-600 text-white'
                      : 'hover:bg-gray-700 text-gray-300'
                  }`}
                >
                  {name}
                </button>
              ))}
            </div>
          )}

          {/* Download button */}
          <button
            onClick={handleDownload}
            disabled={!fileBlob}
            className="flex items-center gap-2 px-3 py-1.5 bg-blue-600 hover:bg-blue-700 rounded-lg transition-colors disabled:opacity-50"
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
            </svg>
            <span className="text-sm">{t('file_preview.download')}</span>
          </button>
        </div>
      </div>

      {/* Content */}
      <div className="flex-1 overflow-hidden">
        {renderContent()}
      </div>

      {/* XLSX Styles */}
      <style>{`
        .xlsx-preview table {
          border-collapse: collapse;
          width: 100%;
          font-size: 14px;
        }
        .xlsx-preview th,
        .xlsx-preview td {
          border: 1px solid #e5e7eb;
          padding: 8px 12px;
          text-align: left;
        }
        .xlsx-preview th {
          background-color: #f9fafb;
          font-weight: 600;
        }
        .xlsx-preview tr:hover {
          background-color: #f3f4f6;
        }
        .docx-preview-container {
          padding: 20px;
        }
      `}</style>
    </div>
  );
}
